import EdTypography from '@Components/Utilities/EdTypography/EdTypography'
import { RaisedButton } from '@eduact/ed-components'
import { FlexLayout } from '@eduact/ed-system'
import React, { useEffect } from 'react'
import { FallbackProps } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'

/**
 * A lazy route chunk that no longer exists on the server: the browser is
 * holding a cached index.html from before a deploy, so it asks for hashed
 * files the new build replaced.
 */
const isChunkLoadError = (error?: Error) =>
	!!error && (error.name === 'ChunkLoadError' || /Loading (CSS )?chunk [^\s]+ failed/i.test(error.message ?? ''))

/** Guards against reloading forever when the fresh document fails too. */
const RELOAD_ONCE_KEY = 'chunk-reload-attempted'

/**
 * Reload with a cache-busting URL, NOT location.reload().
 *
 * A plain reload can be answered from the browser's cache with the very
 * document that references the missing chunks, so the error reproduces and the
 * student is stuck until they clear site data by hand. A URL the cache has
 * never seen forces a fresh document.
 */
const reloadFromServer = () => {
	const url = new URL(window.location.href)
	url.searchParams.set('_r', Date.now().toString(36))
	window.location.replace(url.toString())
}

type Props = {} & FallbackProps
const ErrorBoundaryFallback: React.FC<Props> = ({ error, resetErrorBoundary, children }) => {
	const { t } = useTranslation('generic')
	const chunkError = isChunkLoadError(error)

	useEffect(() => {
		if (!chunkError) return
		let attempted: string | null = null
		try {
			attempted = sessionStorage.getItem(RELOAD_ONCE_KEY)
			if (!attempted) sessionStorage.setItem(RELOAD_ONCE_KEY, '1')
		} catch {
			// private mode / storage disabled: fall through to the manual button
			return
		}
		if (!attempted) reloadFromServer()
	}, [chunkError])

	return (
		<FlexLayout alignItems={'center'} justifyContent={'center'} flex={'1'}>
			<FlexLayout alignItems={'center'} flexDirection={'column'} gridGap="2rem">
				<EdTypography color="red" fontWeight="bold" fontSize="2rem">
					{t('error_occurred')}
				</EdTypography>
				<EdTypography>{error.name}</EdTypography>
				{/* A stale-build error is not resettable in place — the missing chunk
				    has to come from the server. */}
				<RaisedButton onClick={() => (chunkError ? reloadFromServer() : resetErrorBoundary())}>{t('retry')}</RaisedButton>
			</FlexLayout>
		</FlexLayout>
	)
}

export default ErrorBoundaryFallback
